import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  CreateSecretRequest,
  DidVerificationMethodRequest,
  DidVerificationMethodResponse,
  GenerateKeyPairRequest,
  ImportKeyPairRequest,
  KeyPairListResponse,
  KeyPairMetadata,
  KeyPairResponse,
  KeystoreItemListResponse,
  NamespaceProvisioningResponse,
  SecretListResponse,
  SecretMetadata,
  SecretResponse,
  UpdateKeyPairRequest,
  UpdateSecretRequest
} from '../models/keystore-api.models';

@Injectable({ providedIn: 'root' })
export class KeystoreApiService {
  private readonly baseUrl = '/proxy/api/v0/keystoreservice/api/v1/organisations';

  constructor(private readonly http: HttpClient) {}

  listItems(organisationId: number): Observable<KeystoreItemListResponse> {
    return this.http.get<KeystoreItemListResponse>(`${this.keystoreUrl(organisationId)}/items`);
  }

  provisionNamespace(organisationId: number): Observable<NamespaceProvisioningResponse> {
    return this.http.post<NamespaceProvisioningResponse>(
      `${this.keystoreUrl(organisationId)}/namespace`,
      null
    );
  }

  listSecrets(organisationId: number): Observable<SecretListResponse> {
    return this.http.get<SecretListResponse>(`${this.keystoreUrl(organisationId)}/secrets`);
  }

  createSecret(organisationId: number, request: CreateSecretRequest): Observable<SecretMetadata> {
    return this.http.post<SecretMetadata>(`${this.keystoreUrl(organisationId)}/secrets`, request);
  }

  getSecret(organisationId: number, secretId: string): Observable<SecretResponse> {
    return this.http.get<SecretResponse>(`${this.secretUrl(organisationId, secretId)}`);
  }

  getSecretMetadata(organisationId: number, secretId: string): Observable<SecretMetadata> {
    return this.http.get<SecretMetadata>(`${this.secretUrl(organisationId, secretId)}/metadata`);
  }

  updateSecret(
    organisationId: number,
    secretId: string,
    request: UpdateSecretRequest
  ): Observable<SecretMetadata> {
    return this.http.patch<SecretMetadata>(this.secretUrl(organisationId, secretId), request);
  }

  deleteSecret(organisationId: number, secretId: string): Observable<void> {
    return this.http.delete<void>(this.secretUrl(organisationId, secretId));
  }

  listKeyPairs(organisationId: number): Observable<KeyPairListResponse> {
    return this.http.get<KeyPairListResponse>(`${this.keystoreUrl(organisationId)}/key-pairs`);
  }

  importKeyPair(organisationId: number, request: ImportKeyPairRequest): Observable<KeyPairMetadata> {
    return this.http.post<KeyPairMetadata>(`${this.keystoreUrl(organisationId)}/key-pairs`, request);
  }

  inspectPrivateKey(organisationId: number, privateKeyPem: string): Observable<{ algorithm: import('../models/keystore-api.models').KeyAlgorithm; publicKeyPem: string }> {
    return this.http.post<any>(`${this.keystoreUrl(organisationId)}/key-pairs/inspect`, { privateKeyPem });
  }

  generateKeyPair(
    organisationId: number,
    request: GenerateKeyPairRequest
  ): Observable<KeyPairMetadata> {
    return this.http.post<KeyPairMetadata>(
      `${this.keystoreUrl(organisationId)}/key-pairs/generate`,
      request
    );
  }

  getKeyPair(organisationId: number, keyPairId: string): Observable<KeyPairResponse> {
    return this.http.get<KeyPairResponse>(this.keyPairUrl(organisationId, keyPairId));
  }

  getKeyPairMetadata(organisationId: number, keyPairId: string): Observable<KeyPairMetadata> {
    return this.http.get<KeyPairMetadata>(`${this.keyPairUrl(organisationId, keyPairId)}/metadata`);
  }

  updateKeyPair(
    organisationId: number,
    keyPairId: string,
    request: UpdateKeyPairRequest
  ): Observable<KeyPairMetadata> {
    return this.http.patch<KeyPairMetadata>(this.keyPairUrl(organisationId, keyPairId), request);
  }

  deleteKeyPair(organisationId: number, keyPairId: string): Observable<void> {
    return this.http.delete<void>(this.keyPairUrl(organisationId, keyPairId));
  }

  addDidVerificationMethod(
    organisationId: number,
    keyPairId: string,
    request: DidVerificationMethodRequest
  ): Observable<DidVerificationMethodResponse> {
    return this.http.post<DidVerificationMethodResponse>(
      `${this.keyPairUrl(organisationId, keyPairId)}/did-verification-methods`,
      request
    );
  }

  removeDidVerificationMethod(
    organisationId: number,
    keyPairId: string,
    request: DidVerificationMethodRequest
  ): Observable<DidVerificationMethodResponse> {
    return this.http.request<DidVerificationMethodResponse>(
      'DELETE',
      `${this.keyPairUrl(organisationId, keyPairId)}/did-verification-methods`,
      { body: request }
    );
  }

  private keystoreUrl(organisationId: number): string {
    return `${this.baseUrl}/${organisationId}/keystore`;
  }

  private secretUrl(organisationId: number, secretId: string): string {
    return `${this.keystoreUrl(organisationId)}/secrets/${encodeURIComponent(secretId)}`;
  }

  private keyPairUrl(organisationId: number, keyPairId: string): string {
    return `${this.keystoreUrl(organisationId)}/key-pairs/${encodeURIComponent(keyPairId)}`;
  }
}
